import { queryOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query'
import {
  useUserAddressParams,
  useZipForZipOnlySignedInUser,
} from '@/features/account/services/User/hooks'
import {
  fetchShoppingStoreById,
  fetchShoppingStores,
} from '@/features/shop/services/ShoppingStore/fetchers'
import {
  type ShoppingStoreParams,
  type ShoppingStoresResponse,
} from '@/features/shop/services/ShoppingStore/types'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'

export type ShoppingStoreByIdQueryKey = readonly [string, { storeId: number }]
export const shoppingStoresByIdQueryOptions = (storeId: number) =>
  queryOptions({
    queryKey: ['Shopping store by id', { storeId }] as const,
    queryFn: fetchShoppingStoreById,
  })

export const useQueryShoppingStoreById = (storeId: number, enabled = true) => {
  const { userId } = useSession()

  return useQuery({
    ...shoppingStoresByIdQueryOptions(storeId),
    enabled: Boolean(storeId && userId && enabled),
  })
}

export type ShoppingStoresKey = readonly [string, ShoppingStoreParams]

type ShoppingStoresProps<T = ShoppingStoresResponse> = {
  zip?: string
  notifyOnChangeProps?: UseQueryOptions['notifyOnChangeProps']
  isDisabled?: boolean
  selectFn?: (data: ShoppingStoresResponse) => T
}

export const shoppingStoresQueryOptions = (
  params: ShoppingStoreParams,
  additionalParams?: { preferredStoreLocationId: number },
) =>
  // additionalParams is excluded from the query key since it is only used for the
  // PLA region flow so far and it should not affect the caching behavior of the query
  // oxlint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: ['Stores and Pickups', params] as const,
    queryFn: (ctx) => fetchShoppingStores(ctx, additionalParams),
  })

export const useQueryShoppingStores = <T = ShoppingStoresResponse>({
  zip,
  isDisabled,
  notifyOnChangeProps,
  selectFn,
}: ShoppingStoresProps<T> = {}) => {
  const { userId } = useSession()
  const C360ProvidedZip = useZipForZipOnlySignedInUser()
  const userZip = C360ProvidedZip ?? zip
  const params = useUserAddressParams({ zip: userZip })

  const { isSessionLoading, isSessionValid } = useSession()

  const addressZipCode = ('address_zip_code' in params && params.address_zip_code) || ''

  return useQuery({
    notifyOnChangeProps,
    ...shoppingStoresQueryOptions(params),
    enabled:
      !isDisabled &&
      !isSessionLoading &&
      Boolean(
        isSessionValid
          ? userId && (('address_id' in params && params.address_id) || addressZipCode)
          : addressZipCode,
      ),
    select: selectFn,
  })
}

export const useQueryShoppingStoresByAddressId = (addressId: number) => {
  const { userId } = useSession()
  return useQuery({
    ...shoppingStoresQueryOptions({ address_id: addressId }),
    enabled: Boolean(userId && addressId),
  })
}
